import { Box, CircularProgress, Tab, Tabs } from "@mui/material"
import { DashboardChartContainer } from "@features/results/Result/Dashboard/DashboardChartContainer"
import {
  ChartGroupLayout,
  DashboardTableChartDto,
} from "@features/results/Result/Dashboard/dashboardModel"
import { useGetGraphDataQuery, useGetUserGraphDataQuery } from "@store/services/graphsService"
import TableNodePreview from "@features/results/Result/Dashboard/TableNodePreview"
import { useDashboardConfig } from "@features/results/Result/Dashboard/dashboardConfig"
import { type RunDto } from "@custom-types/modelRun"
import { useEffect, useMemo, useRef, useState } from "react"
import { getRunName } from "@utils/run"
import { useIntersectionObserver } from "@hooks/useIntersectionObserver"

const TableNodePreviewComponent = (props: { run?: RunDto; selectedNodeLabel: string }) => {
  const base_graph_key = props.run?.base_graph_key
  const user_graph_key = props.run?.user_graph_key

  const baseGraphDataQuery = useGetGraphDataQuery(base_graph_key ?? "", {
    skip: !base_graph_key || !!user_graph_key,
  })
  const userGraphDataQuery = useGetUserGraphDataQuery(user_graph_key ?? "", {
    skip: !user_graph_key,
  })

  const graphData = user_graph_key
    ? userGraphDataQuery.data
    : base_graph_key
    ? baseGraphDataQuery.data
    : undefined

  const isFetching = user_graph_key ? userGraphDataQuery.isFetching : baseGraphDataQuery.isFetching

  if (isFetching) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!graphData) {
    return null
  }

  const node = Object.values(graphData.nodeMap).find(
    (graphNode) => graphNode.display.label === props.selectedNodeLabel,
  )

  if (!node?.table) {
    return null
  }

  return <TableNodePreview table={node.table} />
}

export const DashboardTableChart = (props: {
  chart: DashboardTableChartDto
  layout?: ChartGroupLayout
  runs?: RunDto[]
  selectedRunKey?: string
}) => {
  const [cfg, updateCfg] = useDashboardConfig({
    selectedNodes: [props.chart?.Nodes?.[0] || props.chart.NodeSelect?.[0]?.Nodes?.[0] || ""],
  })
  const [selectedTabIndex, setSelectedTabIndex] = useState(0)

  const containerRef = useRef<HTMLDivElement>(null)
  const isVisible = useIntersectionObserver(containerRef)

  const selectedNodeLabel = cfg.selectedNodes?.[0] ?? ""
  const runs = useMemo(() => props.runs ?? ([] as RunDto[]), [props.runs])
  const isCompareResultsTable = props.chart.isCompareResultsChart && runs.length > 0
  const selectedRunForTab = runs[selectedTabIndex]
  const selectedRunForSingleTable = runs.find((run) => run.key === props.selectedRunKey) ?? runs[0]

  useEffect(() => {
    if (!isCompareResultsTable) return

    const defaultTabIndex = runs.findIndex((run) => run.key === props.selectedRunKey)
    setSelectedTabIndex(defaultTabIndex >= 0 ? defaultTabIndex : 0)
  }, [isCompareResultsTable, props.selectedRunKey, runs])

  const handleOptionChange = (nodes: string[]) => {
    updateCfg((draft) => {
      draft.selectedNodes = nodes
    })
  }

  const handleFullscreen = () => {
    updateCfg((draft) => {
      draft.isFullscreen = !draft.isFullscreen
    })
  }

  return (
    <DashboardChartContainer
      chart={props.chart}
      layout={props.layout}
      onOptionChange={({ Nodes }) => handleOptionChange(Nodes)}
      onFullScreen={handleFullscreen}
      runIds={runs.map((run) => run.key)}
      ref={containerRef}
    >
      {isVisible && (
        <>
          {isCompareResultsTable ? (
            <>
              <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}>
                <Tabs
                  value={selectedTabIndex}
                  onChange={(_event, newValue: number) => setSelectedTabIndex(newValue)}
                  variant="scrollable"
                  allowScrollButtonsMobile
                >
                  {runs.map((run) => {
                    const runName = getRunName(run)

                    return (
                      <Tab
                        key={run.key}
                        label={
                          <Box
                            sx={{
                              display: "inline-block",
                              //To long label breaks the layout, so we limit it and add tooltip with full name
                              maxWidth: 300,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {runName}
                          </Box>
                        }
                        sx={{ maxWidth: 440 }}
                      />
                    )
                  })}
                </Tabs>
              </Box>

              {selectedRunForTab ? (
                <TableNodePreviewComponent
                  run={selectedRunForTab}
                  selectedNodeLabel={selectedNodeLabel}
                />
              ) : null}
            </>
          ) : (
            <TableNodePreviewComponent
              selectedNodeLabel={selectedNodeLabel}
              run={selectedRunForSingleTable}
            />
          )}
        </>
      )}
    </DashboardChartContainer>
  )
}
